import type { TrelloRestResolver, TypedJSONObject } from '../types';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import { trelloFetch } from '@trello/fetch';
import type { QueryPublicPluginsArgs } from '../generated';
import { getNetworkClient } from '../getNetworkClient';
import { isQueryInfo } from '../isQueryInfo';

export const publicPluginsResolver: TrelloRestResolver<QueryPublicPluginsArgs> = async (
  organization: {
    id: string;
  },
  args,
  context,
  info,
): Promise<TypedJSONObject> => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;
  const queryParams = args?.preferredLocales
    ? new URLSearchParams({
        preferredLocales: args.preferredLocales,
      })
    : null;
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    queryParams
      ? `/1/plugins/public?${queryParams.toString()}`
      : '/1/plugins/public',
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'publicPlugins',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode) : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const pluginCategoriesResolver: TrelloRestResolver<object> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/plugins/categories');

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'pluginCategories',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return prepareDataForApolloCache(model, rootNode);
  } catch (err) {
    console.error(err);
    return model;
  }
};
